import {
  Component,
  OnInit,
  HostListener,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import DOMPurify from "dompurify";
import { ActivatedRoute, Router } from "@angular/router";
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  FormControl,
} from "@angular/forms";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { SignaturesService } from "../signatures.service";
import { HttpErrorResponse } from "@angular/common/http";
import { Signature, SaveSignatureRequest } from "../signatures.interface";
import { StartCheckingForInstallationInProgressAction } from "../../signature-installation-state/signature-installation-state.actions";
import { Store } from "@ngrx/store";
import { AuthService } from "../../auth/auth.service";
import {
  of,
  merge,
  Observable,
  BehaviorSubject,
  combineLatest,
  forkJoin,
} from "rxjs";
import { catchError, debounceTime, switchMap, map } from "rxjs/operators";
import { VariablePaletteComponent } from "../variable-palette/variable-palette.component";
import { MatDialog } from "@angular/material/dialog";
import { NameInputDialogComponent } from "../../shared/name-input-dialog/name-input-dialog.component";
import { EditorComponent } from "../../shared/editor/editor.component";
import { DigitalCardService } from "../../digital-cards/digital-card.service";
import type { DigitalCardProfileItem } from "../../digital-cards/digital-cards.interface";
import type { EmployeePickerItem } from "../../employees/employees.interface";

@Component({
  selector: "app-signature-edit",
  templateUrl: "./signature-edit.component.html",
  styleUrls: ["./signature-edit.component.scss"],
  standalone: false,
})
export class SignatureEditComponent implements OnInit, AfterViewInit {
  @ViewChild(VariablePaletteComponent)
  variablePalette!: VariablePaletteComponent;
  @ViewChild("signatureEditor") signatureEditor!: EditorComponent;

  activeEditor: unknown = null;

  form = new UntypedFormGroup({
    name: new UntypedFormControl(""),
    content: new UntypedFormControl(""),
    addonBannerId: new UntypedFormControl(null),
    addonAdditionalBannerId: new UntypedFormControl(null),
    addonLinkId: new UntypedFormControl(null),
    addonDisclaimerId: new UntypedFormControl(null),
    userAvatarWidth: new UntypedFormControl(null, Validators.min(0)),
    userAvatarHeight: new UntypedFormControl(null, Validators.min(0)),
    userAvatarBorderRadius: new UntypedFormControl(null, [
      Validators.min(0),
      Validators.max(50),
    ]),
    qrDigitalCardWidth: new UntypedFormControl(null, Validators.min(0)),
    digitalCardProfileId: new UntypedFormControl(null),
    phoneLabel: new UntypedFormControl(null),
    mobileLabel: new UntypedFormControl(null),
    emailLabel: new UntypedFormControl(null),
    customLabel: new UntypedFormControl(null),
    customLabelVariable: new UntypedFormControl(null),
    labelColor: new UntypedFormControl(null),
    labelTextColor: new UntypedFormControl(null),
  });

  footer: Signature;
  updating: boolean;
  loading = true;
  sanitizedContent: SafeHtml;
  generatorUrl: string | null = null;
  activeSection = "addons";
  private pageDataReady = false;
  private addonsReady = false;
  customLabelVariableOptions: string[] = [];
  digitalCardTemplates: DigitalCardProfileItem[] = [];
  digitalCardTemplatesLoading = false;
  digitalCardTemplatesLoaded = false;
  readonly identityLabel = (value: string) => value;
  readonly digitalCardTemplateLabel = (template: DigitalCardProfileItem) =>
    template.displayName || "";
  readonly digitalCardTemplateValue = (template: DigitalCardProfileItem) =>
    template.profileId;

  previewEmployee: EmployeePickerItem | null = null;
  private readonly previewEmployee$ =
    new BehaviorSubject<EmployeePickerItem | null>(null);
  preview$: Observable<SafeHtml>;

  constructor(
    private auth: AuthService,
    private route: ActivatedRoute,
    private service: SignaturesService,
    private toast: ToastrService,
    private router: Router,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    private readonly store: Store,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private digitalCardService: DigitalCardService,
  ) {}

  get digitalCardEnabled(): boolean {
    return !!this.auth.appUser?.optionalInterfaces?.digitalCard;
  }

  @HostListener("document:keydown.enter", ["$event"])
  onKeydownHandler(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const isInEditor =
      target.closest(".editor-wrapper") ||
      target.closest(".tox-tinymce") ||
      target.closest(".code-textarea");

    if (!isInEditor && !this.updating) {
      event.preventDefault();
      this.save();
    }
  }

  ngOnInit(): void {
    const templateId = this.route.snapshot.params["templateId"] as string;
    this.loading = true;
    forkJoin({
      signature: this.service.byId(templateId),
      labelOptions: this.service
        .getCustomLabelVariableOptions()
        .pipe(catchError(() => of([] as string[]))),
      templates: this.digitalCardEnabled
        ? this.digitalCardService
            .getProfiles({ templatesOnly: true })
            .pipe(
              catchError(() =>
                of({ profiles: [] as DigitalCardProfileItem[] }),
              ),
            )
        : of({ profiles: [] as DigitalCardProfileItem[] }),
    }).subscribe(({ signature: f, labelOptions, templates }) => {
      this.footer = f;
      this.form.patchValue({
        name: f.name,
        content: f.content,
        addonBannerId: f.addonBannerId || null,
        addonAdditionalBannerId: f.addonAdditionalBannerId || null,
        addonDisclaimerId: f.addonDisclaimerId || null,
        addonLinkId: f.addonLinkId || null,
        userAvatarWidth: f.userAvatarWidth,
        userAvatarHeight: f.userAvatarHeight,
        userAvatarBorderRadius: f.userAvatarBorderRadius,
        qrDigitalCardWidth: f.qrDigitalCardWidth,
        digitalCardProfileId: f.digitalCardProfileId || null,
        phoneLabel: f.phoneLabel || null,
        mobileLabel: f.mobileLabel || null,
        emailLabel: f.emailLabel || null,
        customLabel: f.customLabel || null,
        customLabelVariable: f.customLabelVariable || null,
        labelColor: f.labelColor || null,
        labelTextColor: f.labelTextColor || null,
      });
      this.customLabelVariableOptions = labelOptions;
      this.digitalCardTemplates = templates.profiles ?? [];
      this.digitalCardTemplatesLoaded = this.digitalCardEnabled;
      if (f.generatorLink) {
        this.generatorUrl =
          "https://generator.gsignature.com/settings/" + f.generatorLink;
      }

      const initial$ = of(this.form.value);
      const changes$ = this.form.valueChanges.pipe(debounceTime(400));
      this.preview$ = combineLatest([
        merge(initial$, changes$),
        this.previewEmployee$,
      ]).pipe(
        switchMap(
          ([values, employee]: [
            Record<string, unknown>,
            EmployeePickerItem | null,
          ]) =>
            this.service.preview({
              ...values,
              ...(employee?.email?.trim()
                ? { previewEmployeeEmail: employee.email.trim() }
                : {}),
            }),
        ),
        map((html: string) =>
          this.sanitizer.bypassSecurityTrustHtml(
            DOMPurify.sanitize(html ?? "", {
              ALLOWED_TAGS: [
                "a",
                "b",
                "br",
                "em",
                "i",
                "img",
                "p",
                "span",
                "strong",
                "table",
                "td",
                "tr",
                "div",
                "ul",
                "ol",
                "li",
              ],
            }),
          ),
        ),
      );
      this.pageDataReady = true;
      this.finishInitialLoad();
    });
  }

  onAddonsReady(): void {
    this.addonsReady = true;
    this.finishInitialLoad();
  }

  private finishInitialLoad(): void {
    if (this.pageDataReady && this.addonsReady) {
      this.loading = false;
    }
  }

  onPreviewEmployeeChange(employee: EmployeePickerItem | null): void {
    this.previewEmployee = employee;
    this.previewEmployee$.next(employee);
  }

  toggleSection(section: "addons" | "dynamicVariables" | "variables"): void {
    this.activeSection = section;
  }

  save(installSignature = false) {
    if (!this.form.get("name")?.value) {
      this.showNameInputDialog(installSignature);
      return;
    }

    this.performSave(installSignature);
  }

  private performSave(installSignature = false) {
    this.updating = true;
    const raw = this.form.getRawValue() as SaveSignatureRequest;
    const formData: SaveSignatureRequest = {
      ...raw,
      addonLinkId: raw.addonLinkId || null,
      addonBannerId: raw.addonBannerId || null,
      addonAdditionalBannerId: raw.addonAdditionalBannerId || null,
      addonDisclaimerId: raw.addonDisclaimerId || null,
      digitalCardProfileId: raw.digitalCardProfileId || null,
      phoneLabel: raw.phoneLabel || null,
      mobileLabel: raw.mobileLabel || null,
      emailLabel: raw.emailLabel || null,
      customLabel: raw.customLabel || null,
      customLabelVariable: raw.customLabelVariable || null,
      labelColor: raw.labelColor || null,
      labelTextColor: raw.labelTextColor || null,
    };
    const templateId = this.route.snapshot.params["templateId"] as string;

    void this.service
      .update(templateId, formData, installSignature)
      .toPromise()
      .then(() =>
        this.toast.success(this.translate.instant("footers.updated") as string),
      )
      .then(() => {
        if (
          !installSignature ||
          (this.footer.numberOfInstallations &&
            this.footer.numberOfInstallations > 0) ||
          (this.footer.numberOfAliases && this.footer.numberOfAliases > 0) ||
          (this.footer.numberOfInternalInstallations &&
            this.footer.numberOfInternalInstallations > 0) ||
          (this.footer.numberOfInternalAliases &&
            this.footer.numberOfInternalAliases > 0)
        ) {
          this.goToSignatures();
        } else {
          this.goToSignatureDetails();
        }
      })
      .catch((error: HttpErrorResponse) => {
        if (error.status > 0 && error.status < 500) {
          const body = error.error as { message?: string } | string | null;
          const message =
            typeof body === "object" && body && typeof body.message === "string"
              ? body.message
              : typeof body === "string"
                ? body
                : null;
          this.toast.error(
            message ||
              (this.translate.instant("footers.updateError") as string),
          );
        }
      })
      .finally(() => (this.updating = false));
  }

  private showNameInputDialog(installSignature = false) {
    const dialogRef = this.dialog.open(NameInputDialogComponent, {
      width: "500px",
      data: {
        title: this.translate.instant("footers.enterName") as string,
        placeholder: this.translate.instant("footers.name") as string,
        currentName: (this.form.get("name")?.value as string) || "",
        actionType: installSignature
          ? ("saveAndInstall" as const)
          : ("save" as const),
        saveButtonText: this.translate.instant("save") as string,
        saveAndInstallButtonText: this.translate.instant(
          (this.footer?.numberOfInstallations &&
            this.footer.numberOfInstallations > 0) ||
            (this.footer?.numberOfAliases && this.footer.numberOfAliases > 0) ||
            (this.footer?.numberOfInternalInstallations &&
              this.footer.numberOfInternalInstallations > 0) ||
            (this.footer?.numberOfInternalAliases &&
              this.footer.numberOfInternalAliases > 0)
            ? "saveAndReinstall"
            : "saveAndInstall",
        ) as string,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: { name?: string; action?: string } | undefined) => {
        if (result) {
          this.form.get("name")?.setValue(result.name || "");
          if (result.action === "save") {
            this.performSave(false);
          } else if (result.action === "saveAndInstall") {
            this.performSave(true);
          }
        }
      });
  }

  goToSignatures = () => {
    void this.router.navigate(["signatures"], {
      fragment: this.route.snapshot.params["templateId"] as string,
    });
  };

  goToSignatureDetails = () => {
    void this.router.navigate([
      "signatures",
      this.route.snapshot.params["templateId"] as string,
    ]);
  };

  redirectToGenerator() {
    const appUser = this.auth.appUser;
    if (appUser && this.generatorUrl) {
      this.auth.getGeneratorToken(appUser).subscribe((token) => {
        const url = `${this.generatorUrl}?token=${token}`;
        window.open(url, "_blank");
      });
    }
  }

  saveAndInstall() {
    if (
      (this.footer.numberOfInstallations &&
        this.footer.numberOfInstallations > 0) ||
      (this.footer.numberOfAliases && this.footer.numberOfAliases > 0) ||
      (this.footer.numberOfInternalInstallations &&
        this.footer.numberOfInternalInstallations > 0) ||
      (this.footer.numberOfInternalAliases &&
        this.footer.numberOfInternalAliases > 0)
    ) {
      this.store.dispatch(new StartCheckingForInstallationInProgressAction());
    }

    if (!this.form.get("name")?.value) {
      this.showNameInputDialog(true);
      return;
    }

    this.performSave(true);
  }

  ngAfterViewInit(): void {
    if (this.signatureEditor) {
      this.activeEditor = this.signatureEditor.getEditorInstance();
      this.cdr.detectChanges();
    }
  }

  getActiveEditor(): { insertVariable?: (text: string) => void } | null {
    return this.activeEditor;
  }

  loadDigitalCardTemplates(): void {
    if (this.digitalCardTemplatesLoaded || this.digitalCardTemplatesLoading) {
      return;
    }
    this.digitalCardTemplatesLoading = true;
    this.digitalCardService.getProfiles({ templatesOnly: true }).subscribe({
      next: (res) => {
        this.digitalCardTemplates = res.profiles ?? [];
        this.digitalCardTemplatesLoaded = true;
        this.digitalCardTemplatesLoading = false;
      },
      error: () => {
        this.digitalCardTemplatesLoading = false;
      },
    });
  }

  getDigitalCardTemplateDisplayName(profileId: string | null): string {
    if (!profileId) return "";
    const t = this.digitalCardTemplates.find((p) => p.profileId === profileId);
    return t?.displayName ?? profileId;
  }

  get contentControl(): FormControl<string> {
    return this.form.get("content") as FormControl<string>;
  }

  private normalizeHex(v: string): string {
    const s = v.replace(/^#/, "").trim();
    if (/^[0-9a-fA-F]{6}$/.test(s)) return "#" + s;
    if (/^[0-9a-fA-F]{3}$/.test(s))
      return "#" + s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
    return v.startsWith("#") ? v : "#" + v;
  }

  private isValidHex(v: string): boolean {
    const s = v.startsWith("#") ? v.slice(1) : v;
    return /^[0-9a-fA-F]{3}$/.test(s) || /^[0-9a-fA-F]{6}$/.test(s);
  }

  onLabelColorChange(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.form.patchValue({ labelColor: this.normalizeHex(v) });
  }

  onLabelColorHexBlur(event: Event): void {
    const v = (event.target as HTMLInputElement).value?.trim() || "";
    if (this.isValidHex(v)) {
      this.form.patchValue({ labelColor: this.normalizeHex(v) });
    }
  }

  onLabelTextColorChange(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.form.patchValue({ labelTextColor: this.normalizeHex(v) });
  }

  onLabelTextColorHexBlur(event: Event): void {
    const v = (event.target as HTMLInputElement).value?.trim() || "";
    if (this.isValidHex(v)) {
      this.form.patchValue({ labelTextColor: this.normalizeHex(v) });
    }
  }
}
